import {
  AddIconData,
  AddReactionIconData,
  ArrowOutwardIconData,
  BrushIconData,
  CategoryIconData,
  CircleIconData,
  Crop169IconData,
  FormatAlignCenterIconData,
  FormatAlignLeftIconData,
  FormatAlignRightIconData,
  GridOffIconData,
  GridOnIconData,
  StickyNote2IconData,
  TitleIconData,
} from "@meiden-connect/material-symbols";
import { BinarySwitch, Button, Icon } from "@meiden-connect/ui";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import type { StampKind, TextAlign } from "@refinement-canvas/domain";
import { useCanvasStore, type CanvasTool, type ShapeKind } from "../store/canvas-store";
import { STAMP_KIND_GLYPH, STAMP_KIND_LABEL } from "../objects/stamp-object";
import {
  addPenCustomColor,
  isValidHexColor,
  loadPenCustomColors,
  normalizeHexColor,
  PEN_PRESET_COLORS,
  savePenColor,
  type PenColor,
} from "../../../lib/pen-preference";
import {
  addStickyCustomColor,
  loadStickyCustomColors,
  saveStickyColor,
  STICKY_PRESET_COLORS,
  type StickyColor,
} from "../../../lib/sticky-preference";
import {
  clampTextFontSize,
  resolveTextSizeKind,
  TEXT_SIZE_MAX_PX,
  TEXT_SIZE_MIN_PX,
  TEXT_SIZE_PRESET_PX,
  type TextSizeKind,
} from "../../../lib/text-size-preference";

// 下部中央のフローティングツールバー。
// 図形・ペン・付箋はサブ選択パネルで種類や色を選択できる。
// ペン・付箋の色はボタンではなく丸い色サンプルの直接選択 UI とし、右端の「＋」で
// 任意のカラーパレットからカスタム色を 1 件追加できる（追加色はローカル保持）。
export function ToolBar(): JSX.Element {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const setShapeKind = useCanvasStore((s) => s.setShapeKind);
  const penColor = useCanvasStore((s) => s.penColor);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);
  const stampKind = useCanvasStore((s) => s.stampKind);
  const setStampKind = useCanvasStore((s) => s.setStampKind);
  const textFontSize = useCanvasStore((s) => s.textFontSize);
  const setTextFontSize = useCanvasStore((s) => s.setTextFontSize);
  const textAlign = useCanvasStore((s) => s.textAlign);
  const setTextAlign = useCanvasStore((s) => s.setTextAlign);
  const gridSnapEnabled = useCanvasStore((s) => s.gridSnapEnabled);
  const setGridSnapEnabled = useCanvasStore((s) => s.setGridSnapEnabled);

  // カスタム色一覧はローカルストレージで保持しつつ React state に二重持ちし、
  // 「追加 → 即座に丸として表示」を React 経由で反映できるようにする。
  const [penCustomColors, setPenCustomColors] = useState<readonly PenColor[]>(() =>
    loadPenCustomColors()
  );
  const [stickyCustomColors, setStickyCustomColors] = useState<readonly StickyColor[]>(() =>
    loadStickyCustomColors()
  );

  // 他タブで色を追加された場合に追従できるよう、storage イベントで再ロードする。
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key.startsWith("refinement-canvas:pen:custom-colors")) {
        setPenCustomColors(loadPenCustomColors());
      }
      if (event.key === null || event.key.startsWith("refinement-canvas:sticky:custom-colors")) {
        setStickyCustomColors(loadStickyCustomColors());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const buttons: { tool: CanvasTool; label: string; icon: string }[] = [
    { tool: "place-sticky", label: "付箋を配置", icon: StickyNote2IconData },
    { tool: "place-shape", label: "図形を配置", icon: CategoryIconData },
    { tool: "place-text", label: "テキストを配置", icon: TitleIconData },
    { tool: "draw-pen", label: "ペンで描画", icon: BrushIconData },
    { tool: "place-stamp", label: "スタンプを配置", icon: AddReactionIconData },
  ];

  const choosePenColor = (color: PenColor): void => {
    setPenColor(color);
    savePenColor(color);
  };
  const chooseStickyColor = (color: StickyColor): void => {
    setStickyColor(color);
    saveStickyColor(color);
  };

  // 「＋」を押した時のカスタム色追加。<input type="color"> のネイティブピッカーで
  // 選択させ、確定（change）時にローカルストレージへ追加して即座にツールバーへ反映する。
  const handleAddPenColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addPenCustomColor(normalized);
    setPenCustomColors(next);
    // 追加直後に選択状態へ昇格させ、利用者の意図どおりすぐ描画に使えるようにする。
    choosePenColor(normalized);
  };
  const handleAddStickyColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addStickyCustomColor(normalized);
    setStickyCustomColors(next);
    chooseStickyColor(normalized);
  };

  return (
    <div className="canvas-toolbar-shell" role="group" aria-label="キャンバスツール">
      {tool === "place-shape" ? (
        <SubPanel label="図形の種類">
          {SHAPE_OPTIONS.map((option) => (
            <Button
              key={option.kind}
              type="button"
              variant={shapeKind === option.kind ? "action emphasis" : "action"}
              aria-label={option.label}
              aria-pressed={shapeKind === option.kind}
              title={option.label}
              onClick={() => setShapeKind(option.kind)}
            >
              <Icon d={option.icon} />
            </Button>
          ))}
        </SubPanel>
      ) : null}
      {tool === "draw-pen" ? (
        <SubPanel label="ペンの色">
          <ColorSwatchList
            ariaLabel="ペンの色"
            presetColors={PEN_PRESET_COLORS}
            customColors={penCustomColors}
            value={penColor}
            onChoose={choosePenColor}
            onAddColor={handleAddPenColor}
          />
        </SubPanel>
      ) : null}
      {tool === "place-sticky" ? (
        <>
          <SubPanel label="付箋の色">
            <ColorSwatchList
              ariaLabel="付箋の色"
              presetColors={STICKY_PRESET_COLORS}
              customColors={stickyCustomColors}
              value={stickyColor}
              onChoose={chooseStickyColor}
              onAddColor={handleAddStickyColor}
            />
          </SubPanel>
          <SubPanel label="文字寄せ">
            <TextAlignChooser value={textAlign} onChange={setTextAlign} />
          </SubPanel>
        </>
      ) : null}
      {tool === "place-text" ? (
        <>
          <SubPanel label="文字サイズ">
            <TextSizeChooser value={textFontSize} onChange={setTextFontSize} />
          </SubPanel>
          <SubPanel label="文字寄せ">
            <TextAlignChooser value={textAlign} onChange={setTextAlign} />
          </SubPanel>
        </>
      ) : null}
      {tool === "place-stamp" ? (
        <SubPanel label="スタンプ">
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {STAMP_KIND_ORDER.map((kind) => (
              <Button
                key={kind}
                type="button"
                variant={stampKind === kind ? "action emphasis" : "action"}
                aria-label={STAMP_KIND_LABEL[kind]}
                aria-pressed={stampKind === kind}
                title={STAMP_KIND_LABEL[kind]}
                onClick={() => setStampKind(kind)}
              >
                <span style={{ fontSize: "18px", lineHeight: 1 }}>{STAMP_KIND_GLYPH[kind]}</span>
              </Button>
            ))}
          </div>
        </SubPanel>
      ) : null}
      <div role="toolbar" aria-label="キャンバスツールバー" className="canvas-toolbar">
        {/* グリッド吸着の ON/OFF。付箋ボタンの左に配置し、設定はローカルストレージに保存する。 */}
        <BinarySwitch
          aria-label="グリッド吸着の有効/無効"
          className="canvas-grid-snap-switch"
          data-testid="grid-snap-switch"
          onChange={setGridSnapEnabled}
          options={[
            { d: GridOffIconData, value: false },
            { d: GridOnIconData, value: true },
          ]}
          title={gridSnapEnabled ? "グリッド吸着: ON" : "グリッド吸着: OFF"}
          value={gridSnapEnabled}
        />
        {buttons.map((b) => (
          <Button
            key={b.tool}
            type="button"
            aria-label={b.label}
            aria-pressed={tool === b.tool}
            title={b.label}
            onClick={() => setTool(tool === b.tool ? "idle" : b.tool)}
            data-testid={`tool-button-${b.tool}`}
            variant={tool === b.tool ? "action emphasis" : "action"}
          >
            <Icon d={b.icon} />
          </Button>
        ))}
      </div>
    </div>
  );
}

function SubPanel({ children, label }: { readonly children: ReactNode; readonly label: string }): JSX.Element {
  return (
    <div className="canvas-toolbar-subpanel" aria-label={label}>
      <span className="canvas-toolbar-subpanel-label">{label}</span>
      <div className="canvas-toolbar-subpanel-actions">{children}</div>
    </div>
  );
}

// 色のサンプル丸ボタン群。
// プリセット色 → カスタム色 → 「＋」追加ボタン の順に並べる。
// 選択中の丸には外側の青い円形リングを描いて選択状態を強調する（添付イメージ準拠）。
function ColorSwatchList({
  ariaLabel,
  presetColors,
  customColors,
  value,
  onChoose,
  onAddColor,
}: {
  readonly ariaLabel: string;
  readonly presetColors: readonly string[];
  readonly customColors: readonly string[];
  readonly value: string;
  readonly onChoose: (color: string) => void;
  readonly onAddColor: (color: string) => void;
}): JSX.Element {
  const normalizedValue = normalizeHexColor(value);
  // 重複排除のため、プリセットに既に含まれている custom 色は表示しない（loadX で既に正規化済み）。
  const visibleCustom = customColors.filter((c) => !presetColors.includes(c));

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "center",
        maxWidth: "440px",
      }}
    >
      {presetColors.map((color) => (
        <ColorSwatch
          key={`preset-${color}`}
          color={color}
          selected={normalizeHexColor(color) === normalizedValue}
          onClick={() => onChoose(color)}
        />
      ))}
      {visibleCustom.map((color) => (
        <ColorSwatch
          key={`custom-${color}`}
          color={color}
          selected={normalizeHexColor(color) === normalizedValue}
          onClick={() => onChoose(color)}
        />
      ))}
      <AddColorSwatch onPick={onAddColor} />
    </div>
  );
}

// 色サンプル 1 件分。<input type="radio"> をベースに視覚は丸い色見本へ差し替える。
function ColorSwatch({
  color,
  selected,
  onClick,
}: {
  readonly color: string;
  readonly selected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const swatchStyle: CSSProperties = {
    position: "relative",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: color,
    border: "1px solid rgba(0,0,0,0.08)",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={color}
      title={color}
      onClick={onClick}
      style={swatchStyle}
    />
  );
}

// 「＋」ボタンに見える色追加トリガ。実体は隠した <input type="color">。
// クリックでネイティブカラーピッカーを開き、change で hex 値を上位へ通知する。
function AddColorSwatch({ onPick }: { readonly onPick: (color: string) => void }): JSX.Element {
  return (
    <label
      title="カスタム色を追加"
      aria-label="カスタム色を追加"
      style={{
        position: "relative",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        border: "1.5px dashed var(--color-border, #b6b6c6)",
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        color: "var(--color-text-muted, #6b6f7d)",
        background: "transparent",
      }}
    >
      <Icon d={AddIconData} />
      <input
        type="color"
        // 透明 + 大きさ 0 でネイティブピッカーだけを利用する。クリックは label のヒット領域で拾う。
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          width: "100%",
          height: "100%",
          padding: 0,
          border: "none",
          cursor: "pointer",
        }}
        onChange={(event) => onPick(event.currentTarget.value)}
      />
    </label>
  );
}

// 文字サイズの 2 段階目選択 UI。
// 小・中・大 のプリセットボタンと、「px」入力（数値スピン）を横並びで提供する。
// プリセット値と一致する px の場合は該当ボタンを選択状態として強調する。
// px 入力は途中の文字列を local state（draft）で保持し、blur / Enter / 矢印操作で
// 確定時にだけ clamp して onChange へ反映する。これをしないと、たとえば「24」と打つ
// 途中の「2」が即 clamp（=8）されて以降のキーストロークが破棄されてしまうため、
// 利用者が複数桁の値を入力できなくなる。
function TextSizeChooser({
  value,
  onChange,
}: {
  readonly value: number;
  readonly onChange: (px: number) => void;
}): JSX.Element {
  const kind = resolveTextSizeKind(value);
  // value（外部）と pxDraft（ローカル文字列）を二重持ちし、外部値の更新があれば draft を
  // 同期する。利用者の途中入力中は draft が外部値より先行する。
  const [pxDraft, setPxDraft] = useState<string>(() => String(value));
  useEffect(() => {
    setPxDraft(String(value));
  }, [value]);

  const commitDraft = (): void => {
    const next = clampTextFontSize(Number.parseInt(pxDraft, 10));
    setPxDraft(String(next));
    if (next !== value) onChange(next);
  };

  const presetButton = (id: TextSizeKind, label: string, px: number): JSX.Element => (
    <Button
      key={id}
      type="button"
      variant={kind === id ? "action emphasis" : "action"}
      aria-label={`文字サイズ: ${label}（${px}px）`}
      aria-pressed={kind === id}
      title={`${label}（${px}px）`}
      onClick={() => onChange(px)}
    >
      <span style={{ minWidth: "1.2em", textAlign: "center" }}>{label}</span>
    </Button>
  );
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {presetButton("small", "小", TEXT_SIZE_PRESET_PX.small)}
      {presetButton("medium", "中", TEXT_SIZE_PRESET_PX.medium)}
      {presetButton("large", "大", TEXT_SIZE_PRESET_PX.large)}
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px",
          color: "var(--color-text-muted, #6b6f7d)",
        }}
        title="px 指定"
      >
        <input
          type="number"
          inputMode="numeric"
          min={TEXT_SIZE_MIN_PX}
          max={TEXT_SIZE_MAX_PX}
          step={1}
          value={pxDraft}
          aria-label="文字サイズ（px 指定）"
          onChange={(event) => {
            // 途中の入力（例: "24" を打つ途中の "2"）も draft として受け入れる。
            // clamp は blur / Enter / 矢印操作の確定時に行う。
            setPxDraft(event.currentTarget.value);
          }}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
          style={{
            width: "64px",
            padding: "4px 6px",
            border: "1px solid var(--color-border, #b6b6c6)",
            borderRadius: "var(--radius-1, 4px)",
            font: "inherit",
            color: "inherit",
            background: kind === "custom" ? "var(--color-surface, #fff)" : "transparent",
          }}
        />
        <span>px</span>
      </label>
    </div>
  );
}

// 文字寄せ（左/中央/右）の選択肢。テキスト・付箋の挿入前設定に使う。
const TEXT_ALIGN_OPTIONS: readonly {
  readonly value: TextAlign;
  readonly label: string;
  readonly icon: string;
}[] = [
  { value: "left", label: "左寄せ", icon: FormatAlignLeftIconData },
  { value: "center", label: "中央寄せ", icon: FormatAlignCenterIconData },
  { value: "right", label: "右寄せ", icon: FormatAlignRightIconData },
];

// 文字寄せ選択 UI。左/中央/右のアイコンボタンを横並びで提供し、選択中を強調する。
function TextAlignChooser({
  value,
  onChange,
}: {
  readonly value: TextAlign;
  readonly onChange: (align: TextAlign) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {TEXT_ALIGN_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          aria-label={option.label}
          aria-pressed={value === option.value}
          title={option.label}
          onClick={() => onChange(option.value)}
        >
          <Icon d={option.icon} />
        </Button>
      ))}
    </div>
  );
}

// スタンプ選択ボタンの並び順（要望の 5 種類）。
const STAMP_KIND_ORDER: readonly StampKind[] = ["thumbs-up", "fire", "smile", "hundred", "plus"];

const SHAPE_OPTIONS: readonly {
  readonly kind: ShapeKind;
  readonly label: string;
  readonly icon: string;
}[] = [
  { kind: "rectangle", label: "四角", icon: Crop169IconData },
  { kind: "circle", label: "丸", icon: CircleIconData },
  { kind: "arrow", label: "矢印", icon: ArrowOutwardIconData },
];
